import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { hideDialog } from '../../../dialog/actions';
import { IconCloseLarge } from '../../../icons/svg';
import { operatesWithEnterKey } from '../../functions.web';
import BaseDialog from './BaseDialog';
import Button from './Button';
import { withPixelLineHeight } from "../../../styles/functions.web";
import { IISightIcon } from "common/components/IISightIcon";
import { Loader } from "common/components/Loader";
const useStyles = makeStyles()(theme => {
    return {
        header: {
            width: "100%",
            padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between"
        },
        title: {
            color: theme.palette.iisightHeader,
            ...withPixelLineHeight(theme.typography.heading5),
            margin: 0,
            padding: 0
        },
        content: {
            height: "auto",
            overflowY: "auto",
            width: "100%",
            boxSizing: "border-box",
            padding: `0 ${theme.spacing(4)} ${theme.spacing(2)}`,
            overflowX: "hidden",
            minHeight: "40px",
            "@media (max-width: 448px)": {
                height: "100%"
            }
        },
        contentWithNoFooter: {
            marginBottom: theme.spacing(2)
        },
        okButton: {
            minWidth: 60
        },
        cancelButton: {
            color: theme.palette.iisightDefaultText
        },
        footer: {
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: `0  ${theme.spacing(4)} ${theme.spacing(3)}`,
            "& button:last-child": {
                marginLeft: "16px"
            }
        }
    };
});
const Dialog = ({ back = { hidden: true }, cancel = { translationKey: 'dialog.Cancel' }, children, className, description, disableAutoHideOnSubmit = false, disableBackdropClose, hideCloseButton, disableEnter, disableEscape, ok = { translationKey: 'dialog.Ok' }, onCancel, onSubmit, size, testId, title, titleKey, loading }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const onClose = useCallback(() => {
        dispatch(hideDialog());
        onCancel?.();
    }, [onCancel]);
    const submit = useCallback(() => {
        if (onSubmit && ((document.activeElement && !operatesWithEnterKey(document.activeElement))
            || !document.activeElement)) {
            !disableAutoHideOnSubmit && dispatch(hideDialog());
            onSubmit();
        }
    }, [onSubmit]);
    const isFooterHidden = back.hidden && cancel.hidden && ok.hidden;
    return ((React.createElement(BaseDialog, { className: className, description: description, disableBackdropClose: disableBackdropClose, disableEnter: disableEnter, disableEscape: disableEscape, onClose: onClose, size: size, submit: submit, testId: testId, title: title, titleKey: titleKey },
        React.createElement("div", { className: classes.header },
            React.createElement("h1", { className: classes.title, id: "dialog-title" }, title ?? t(titleKey ?? "")),
            !hideCloseButton && (React.createElement(IISightIcon, { accessibilityLabel: t("dialog.accessibilityLabel.close"), icon: IconCloseLarge, id: "modal-header-close-button", onClick: onClose }))),
        React.createElement("div", { className: cx(classes.content, isFooterHidden && classes.contentWithNoFooter), "data-autofocus-inside": "true" }, loading ? React.createElement(Loader, { height: 75 }) : children),
        !isFooterHidden && (React.createElement("div", { className: classes.footer, "data-autofocus-inside": "true" },
            !back.hidden && (React.createElement(Button, { accessibilityLabel: t(back.translationKey ?? ""), labelKey: back.translationKey, onClick: back.onClick, type: "secondary" })),
            !cancel.hidden && (React.createElement(Button, { accessibilityLabel: t(cancel.translationKey ?? ""), labelKey: cancel.translationKey, onClick: onClose, className: classes.cancelButton, type: "tertiary" })),
            !ok.hidden && (React.createElement(Button, { accessibilityLabel: t(ok.translationKey ?? ""), disabled: ok.disabled, id: "modal-dialog-ok-button", isSubmit: true, className: classes.okButton, labelKey: ok.translationKey, ...(!ok.disabled && {
                    onClick: submit
                }) })))))));
};
export default Dialog;
